import { LoggerService } from "@bfx/logger";
const logger = LoggerService.for(__dirname);
import { Directive, Input, Host, Optional, Self } from "@angular/core";
import { DOM_HANDLER_MODE_TOKEN, DomHandlerMode, DomHandlerService } from "@bfx/primeng/dom";
import { OverlayPanel } from "@bfx/primeng/overlaypanel";
import { NativeDesktopSupport } from "./native-desktop-support";
import { PointTopLeft } from "openfin/_v2/api/system/point";
import { ResizeObserverScheduler } from "../../shared/helper-services/resize-observer-scheduler";
import { NativeDesktopWindow } from "../../shared/native-desktop/NativeDesktopWindow";
import { WindowType } from "shared/native-desktop/NativeDesktopWindowOptions";
const WINDOW_TITLE = "appNotification";

@Directive({
  selector: "[nativeDesktopAppNotification]",
  providers: [
    {
      provide: DOM_HANDLER_MODE_TOKEN,
      useFactory: () => {
        if (NativeDesktopSupport.isNativeDesktopContainer())
          return new DomHandlerService(DomHandlerMode.NativeContainer);
        return new DomHandlerService(DomHandlerMode.Browser);
      },
    },
  ],
  standalone: false,
})
export class NativeDesktopAppNotification {
  private static appNotificationWindow: NativeDesktopWindow = null;
  private static resizeObserver: ResizeObserverScheduler;

  private _location: string = "body";

  private overlay: OverlayPanel;

  constructor(@Host() @Self() @Optional() component: OverlayPanel) {
    if (NativeDesktopSupport.isNativeDesktopContainer()) {
      this.initialize(component);
    }
  }

  private initialize(component: OverlayPanel): void {
    if (NativeDesktopSupport.isNativeDesktopContainer()) {
      if (component instanceof OverlayPanel) {
        const onAnimationStartFn = component.onAnimationStart.bind(component);
        let pendingRaf = null;
        let mousePosition = null;
        let overlay: OverlayPanel = component as OverlayPanel;

        // main window - not the app-notification window
        let parentWindow = NativeDesktopSupport.getWindow(overlay.el.nativeElement);
        parentWindow.addEventListener("closed", this.onHideOverlay.bind(this, overlay));

        parentWindow.addEventListener("focused", () => {
          let childWindow: NativeDesktopWindow = NativeDesktopAppNotification.appNotificationWindow;

          if (childWindow == null) {
            logger.out.warn(`${this.constructor.name} focused handler couldn't find child window.`);
            return;
          }

          if (!childWindow.getNativeWindow().document.hidden) {
            requestAnimationFrame(() => {
              overlay.hide();
            });
          }
        });

        overlay.el.nativeElement.style.opacity = 0;
        overlay.dismissable = false;

        component.onAnimationStart = (event) => {
          if (pendingRaf != null) return;

          fin.desktop.System.getMousePosition(
            (mp: PointTopLeft) => {
              mousePosition = mp;

              pendingRaf = requestAnimationFrame(() => {
                onAnimationStartFn(event);
                pendingRaf = null;
              });
            },
            (err) => {
              logger.out.warn(`${this.constructor.name} onShow mouse position failed ${err} `);
            },
          );
        };

        overlay.onShow.subscribe(
          function () {
            this.onShowOverlay(overlay, mousePosition);
          }.bind(this),
        );

        overlay.onHide.subscribe(
          function () {
            if (pendingRaf == null) mousePosition = null;

            this.onHideOverlay(overlay);
          }.bind(this),
        );
      } else {
        logger.out.warn(`${this.constructor.name} component not an instance of OverlayPanel`);
      }
    }
  }

  public onShowOverlay(overlayComponent: OverlayPanel, mousePosition: PointTopLeft): void {
    overlayComponent.appendTo = null;

    if (this._location == "target") {
      return;
    }

    if (mousePosition == null) return;

    let element: Element = overlayComponent.el.nativeElement;
    element.removeAttribute("appendTo");
    element["style"].left = element["style"].top = "0px";
    element["style"].opacity = 1;

    let childWindow: NativeDesktopWindow = NativeDesktopAppNotification.appNotificationWindow;
    childWindow.getNativeWindow().document.body.innerHTML = "";
    childWindow.getNativeWindow().document.body.appendChild(element);

    requestAnimationFrame(() => {
      let innerDivElement: Element = null;
      element.childNodes.forEach((node: Node) => {
        if (node instanceof Element) {
          let element: Element = node;
          if (element.tagName.toLowerCase() == "div") {
            innerDivElement = element;
            element.removeAttribute("style");
          }
        }
      });

      if (innerDivElement != null) {
        innerDivElement["style"].boxShadow = "none";
        this.adjustWindow(
          innerDivElement,
          childWindow,
          element,
          overlayComponent,
          mousePosition,
          true,
        );
      }
    });
  }

  private adjustWindow(
    innerDivElement: Element,
    childWindow: NativeDesktopWindow,
    element: Element,
    overlayComponent: OverlayPanel,
    mousePosition: PointTopLeft,
    focus: boolean,
  ): void {
    let width: number = innerDivElement["offsetWidth"] * NativeDesktopSupport.zoomLevel;
    let height: number = innerDivElement["offsetHeight"] * NativeDesktopSupport.zoomLevel;
    let left = mousePosition.left - (width * NativeDesktopSupport.zoomLevel + 16);
    let top = mousePosition.top + 16;
    childWindow.setBounds(left, top, width, height).then(
      () => {
        childWindow.show().then(
          async () => {
            if (focus) {
              await new Promise<void>((res) =>
                childWindow.focus().then(
                  () => res(),
                  () => res(),
                ),
              );
            } else {
              await NativeDesktopSupport.restoreUtility(childWindow);
            }

            NativeDesktopAppNotification.resizeObserver.observe(innerDivElement);
          },
          (err) => {
            logger.out.warn(`${this.constructor.name} childWindow.showAt failed ${err}`);
          },
        );
      },
      (err) => {
        logger.out.warn(`${this.constructor.name} childWindow.setBounds failed ${err}`);
      },
    );
  }

  public onHideOverlay(overlayComponent: OverlayPanel): void {
    if (NativeDesktopAppNotification.appNotificationWindow) {
      let element: Element = overlayComponent.el.nativeElement;
      let innerDivElement: Element = null;
      element.childNodes.forEach((node: Node) => {
        if (node instanceof Element) {
          let element: Element = node;
          if (element.tagName.toLowerCase() == "div") {
            innerDivElement = element;
            element.removeAttribute("style");
          }
        }
      });

      NativeDesktopAppNotification.appNotificationWindow.hide().then(() => {
        if (innerDivElement != null && innerDivElement instanceof Element) {
          NativeDesktopAppNotification.resizeObserver.unobserve(innerDivElement);
        }
      });
    }
  }

  public get location(): string {
    return this._location;
  }

  @Input("nativeDesktopAppNotification")
  public set location(location: string) {
    this._location = location;
    if (
      NativeDesktopSupport.isNativeDesktopContainer() &&
      NativeDesktopAppNotification.appNotificationWindow == null
    ) {
      NativeDesktopSupport.openWindow(
        WINDOW_TITLE,
        WindowType.Utility,
        false,
        null,
        (childWindow: NativeDesktopWindow) => {
          NativeDesktopAppNotification.appNotificationWindow = childWindow;
          childWindow["beforeClose"] = () => {
            // blur of window, close or opening settings will close,
            // return false to prevent NativeDesktopSupport.closeAllSmallWindows() closing this
            return false;
          };

          const overlayListener = function () {
            if (
              this != null &&
              this instanceof NativeDesktopAppNotification &&
              this.overlay != null &&
              this.overlay instanceof OverlayPanel &&
              childWindow != null
            ) {
              this.overlay.hide();
              // clean up listener as window may be reused
              // TODO ELECTRON
              // childWindow.removeEventListener("blurred", overlayListener);
            } else {
              logger.out.warn(
                `${this.constructor.name} failed to hide overlay in response to a blur event`,
              );
            }
          }.bind(this);
          childWindow.addEventListener("blurred", overlayListener);

          if (NativeDesktopAppNotification.resizeObserver != null) {
            NativeDesktopAppNotification.resizeObserver.complete();
          }

          NativeDesktopAppNotification.resizeObserver = new ResizeObserverScheduler(
            (entries) => this.onResize(entries),
            NativeDesktopAppNotification.name,
          );
        },
      );
    }
  }

  public onResize(entries): Promise<void> {
    return new Promise<void>((all) => {
      const resizePromises: Promise<void>[] = [];

      for (const entry of entries) {
        let target: Element = entry.target;
        const { width, height } = entry.contentRect;
        const offsetWidth = target["offsetWidth"];
        const offsetHeight = target["offsetHeight"];
        if (
          NativeDesktopAppNotification.appNotificationWindow == null ||
          width == 0 ||
          height == 0
        ) {
          continue;
        }

        resizePromises.push(
          new Promise<void>((res) => {
            let w: number = offsetWidth * NativeDesktopSupport.zoomLevel;
            let h: number = offsetHeight * NativeDesktopSupport.zoomLevel;
            NativeDesktopAppNotification.appNotificationWindow.setSize(w, h).then(
              () => {
                res();
              },
              (err) => {
                logger.out.error(`${this.constructor.name}: failed resizeTo() due to ${err}`);
                res();
              },
            );
          }),
        );
      }

      Promise.all(resizePromises).then(() => all());
    });
  }

  public static hide(): void {
    if (NativeDesktopAppNotification.appNotificationWindow != null) {
      NativeDesktopAppNotification.appNotificationWindow.hide();
    }
  }
}
